//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

/*
This file is responsible for setting up the global crl8 object and
exposing crl8.session and crl8.sessionPromise. Our older pages use a
lift snippet to expose the session state on the window, but newer pages
make an api call to retrieve the session state, so we expose a promise
to retrieve the session so that all session dependent code can run when
the session state is ready.
 */

import { Auth } from 'aws-amplify';
import * as $ from 'jquery';

import { CuralateAuthTokenHeader } from '@constants/CuralateAuthTokenHeader';

import {
  IBasicCuralateSession,
  ICuralateSession,
} from 'app/basic-session/ICuralateSession';
import { crl8, isAuthedSession } from 'app/curalate.globals';
import { loadBetaVersionIfEligible } from 'app/initialization/session/loadBetaVersionIfEligible';

import shimCuralateEnvSessionVars from 'app/initialization/session/env-session-shim';

async function initSession() {
  let jwt: string | undefined;
  try {
    const cognitoSession = await Auth.currentSession();
    jwt = cognitoSession.getIdToken().getJwtToken();
  } catch (e) {
    crl8.logger.console.debug('No session found, user logged out.', e);
  }
  let session: IBasicCuralateSession | ICuralateSession | undefined;
  try {
    session = await $.ajax({
      url: '/api/session',
      type: 'GET',
      dataType: 'json',
      beforeSend: (xhr) => {
        if (jwt) {
          xhr.setRequestHeader(CuralateAuthTokenHeader, jwt);
        }
      },
    });
  } catch (e) {
    crl8.logger.console.error('Failed to load /api/session', e);
    crl8.sessionPromise.reject(e);
    crl8.userSessionPromise.reject(e);
  }
  if (session) {
    crl8.session = shimCuralateEnvSessionVars(session) as ICuralateSession;

    // For users that don't have an authed session but do have a
    // cognito token, log them out to clear their amplifyStorage and
    // reload the page. This means that /api/session and cognito
    // have desynced
    if (jwt && !crl8.isAuthedSession()) {
      crl8.logger.console.error(
        'User marked as authorized but /api/session was empty. Logging user out.',
      );
      Auth.signOut();
      location.reload();
    } else {
      // If the user is eligible for a Beta version of the frontend, configure
      // them that way and redirect them. If there are currently no feature
      // flags or beta versions of the dashboard, isEligibleForBeta can be set to
      // false. Wrap in a try/catch to avoid breaking the boot process if
      // something goes wrong.
      try {
        const isEligibleForBeta = false;
        loadBetaVersionIfEligible(isEligibleForBeta);
      } catch (e) {
        crl8.logger.console.error(
          'Beta check failed. Continuing boot process without it.',
        );
      }

      // Make sure we resolve the sessionPromise before we bootstrap the app so
      // that all of the session dependent code gets to run before we bootstrap.
      handleSessionPromises(session);
    }
  }
}

// We expose two promises, one for basic session state, and the other for user
// specific state. If the user state is not available, we reject the userSessionPromise.
function handleSessionPromises(
  session: IBasicCuralateSession | ICuralateSession,
) {
  crl8.sessionPromise.resolve(session);

  if (isAuthedSession(session)) {
    crl8.userSessionPromise.resolve(session);
  } else {
    crl8.userSessionPromise.reject(session);
  }

  return crl8.sessionPromise;
}

initSession();
